import * as React from 'react';
import { sendApiRequest } from 'utils/network';
import { ITenant } from './types';

export function NewTenantForm({ onSave, onCancel }: { onSave: (tenant: ITenant) => void; onCancel: () => void }) {
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const elementRef = React.useRef<HTMLFormElement>(null);
  React.useEffect(() => {
    const input = elementRef?.current?.querySelector('#tenant-name') as HTMLInputElement;
    if (input) {
      input.focus();
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const tenantName = formData.get('name')?.toString();

    if (!tenantName) {
      setError('Tenant name is required');
      return;
    }

    setIsSaving(true);

    try {
      const response = await sendApiRequest<ITenant>('/tenants', {
        method: 'POST',
        body: { name: tenantName },
      });
      if (response) {
        requestAnimationFrame(() => onSave(response));
        setError(null);
      }
    } catch (error) {
      console.error('Failed to create tenant:', error);
      setError((error as Error).message ?? error);
    }
    setIsSaving(false);
  };

  return (
    <form
      ref={elementRef}
      onSubmit={handleSubmit}
      className="mx-auto mb-3 max-w-7xl rounded-md bg-teal-dark px-1 py-3 sm:px-2 lg:px-4"
    >
      <div className="space-y-8">
        <div className="border-b border-gray-10050 pb-12">
          <h2 className="text-base/7 font-semibold text-white">New Tenant Information</h2>
          <p className="mt-1 text-sm/6 text-gray-400">
            Add a new client who will integrate 3D Exhibition web components. You can later create configuration for
            that tenant.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-2">
              <label htmlFor="last-name" className="block text-sm/6 font-medium text-white">
                Tenant Name
              </label>
              <div className="mt-2">
                <input
                  id="tenant-name"
                  name="name"
                  type="text"
                  autoComplete="tenant-name"
                  disabled={isSaving}
                  className="bg-white/5 outline-white/10 block w-full rounded-md px-3 py-1.5 text-base text-black outline outline-1 -outline-offset-1 placeholder:text-gray-10050 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-teal-dark sm:text-sm/6"
                />
              </div>
            </div>
          </div>
        </div>
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="shrink-0">
                <svg
                  className="size-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">There was an error with your submission</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          type="button"
          className="text-sm/6 font-semibold text-white disabled:opacity-75"
          disabled={isSaving}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="rounded-md bg-teal px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark enabled:hover:bg-green-700 disabled:opacity-75"
        >
          Save
        </button>
      </div>
    </form>
  );
}
